import type { TOC } from '@ember/component/template-only';
import { concat, hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import {
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { pageTitle } from 'ember-page-title';
import { and, eq, not } from 'ember-truth-helpers';
import ButtonSingleSignOn from 'frontend-login/components/button-single-sign-on';
import LoadingScreen from 'frontend-login/components/loading-screen';
import type LoginController from 'frontend-login/controllers/login';
import { contextualPageTitle } from 'frontend-utils';
import { LoginCard, LoginCardFooter } from 'password-form';

import styles from './login.module.css';

interface LoginSignature {
  controller: LoginController;
}

<template>
  {{pageTitle (contextualPageTitle (t "login.sign_in"))}}

  {{#if
    (and
      @controller.sharedAuth.isRedirecting
      (not @controller.sharedAuth.loadingButtonType)
    )
  }}
    <LoadingScreen
      @isBeingRedirected={{@controller.sharedAuth.isBeingRedirected}}
    />
  {{else if @controller.sharedAuth.connectWithNewPlatformConfirmation}}
    <PrismCard
      class={{styles.confirmation-card}}
      data-test-id="confirmation-card"
    >
      <:header as |Header|>
        <Header.title>
          {{t "general.lighthouse"}}
        </Header.title>
      </:header>
      <:content>
        <p
          class="text-300--regular"
        >{{@controller.newPlatformConfirmationText}}</p>
      </:content>

      <:footer>
        <div class="flex justify-end gap-300">
          <PrismButton
            @buttonType="secondary"
            @label={{t "login.cancel"}}
            @onClick={{@controller.cancelConnectWithNewPlatform}}
          />
          <PrismButton
            @label={{t "login.apply"}}
            @onClick={{@controller.applyConnectWithNewPlatform}}
          />
        </div>
      </:footer>
    </PrismCard>
  {{else}}
    <LoginCard
      @onBackButtonClicked={{@controller.reset}}
      @showBackButton={{@controller.sharedAuth.userHasloginOptions}}
      @showSpiderMigrationMessage={{@controller.spiderMigrationMessage}}
      @title={{@controller.title}}
      {{@controller.initializeErrorHandling}}
    >
      <div class="mt-600 flex flex-col">
        {{#if @controller.sharedAuth.showSSOConfirmationScreen}}
          <div class="mb-600">
            <PrismBanner
              @bannerMessage={{t "login.sso_confirmation"}}
              @bannerStyle="warning"
              @icon="warning"
              data-test-id="banner-confirmation"
            />
          </div>
        {{/if}}
        {{#if @controller.sharedAuth.showError}}
          <div class="mb-600">
            <PrismBanner
              @bannerMessage={{@controller.sharedAuth.errorMessage}}
              @bannerStyle={{@controller.sharedAuth.bannerStyle}}
              @bannerTitle={{@controller.sharedAuth.errorTitle}}
              @icon="warning"
              @link={{@controller.sharedAuth.errorLink}}
              data-test-id="banner-error"
            />
          </div>
        {{/if}}
        {{#if @controller.sharedAuth.show2FAScreen}}
          <div class="mb-600">
            <PrismBanner
              @bannerMessage={{t "login.2fa_message"}}
              @bannerStyle="info"
              @bannerTitle={{t "login.2fa_title"}}
            />
          </div>
        {{/if}}
        <div class="flex flex-col gap-400">
          <PrismInput
            @autofocus={{true}}
            @errorMessage={{t "login.error.email"}}
            @hasErrors={{@controller.sharedAuth.emailInputHasErrors}}
            @label={{t "login.email"}}
            @name="email"
            @onInput={{@controller.sharedAuth.setEmail}}
            @onKeyDown={{@controller.submitFormWithEnter}}
            @removePadding={{true}}
            @type="email"
            @value={{@controller.sharedAuth.emailInputValue}}
            data-test-id="input-email"
          />
          {{#if @controller.shouldRenderPasswordInput}}
            <div class="flex flex-col">
              <PrismInput
                @autofocus={{true}}
                @label={{t "login.method.password"}}
                @onInput={{@controller.sharedAuth.setPassword}}
                @onKeyDown={{@controller.submitFormWithEnter}}
                @removePadding={{true}}
                @required={{true}}
                @requiredErrorMessage={{t "login.error.password"}}
                @type="password"
                @value={{@controller.sharedAuth.password}}
                data-test-id="input-password"
              />
              <div
                class={{concat styles.reset-password-container " " "mt-200"}}
              >
                <p class="mr-50">{{t "login.forgot_your_password"}}</p>
                <LinkTo
                  @query={{hash email=@controller.sharedAuth.emailInputValue}}
                  @route="password-reset"
                  class={{styles.reset-password-link}}
                  data-test-id="link-forgot-password"
                >
                  {{t "login.reset_here"}}
                </LinkTo>
              </div>
            </div>
          {{/if}}
        </div>
        {{#if @controller.sharedAuth.showSSOConfirmationScreen}}
          <div class="mt-400 flex justify-around gap-200">
            <PrismButton
              @buttonType="secondary"
              @label={{t "login.deny"}}
              @onClick={{@controller.denySSOConfirmation}}
              class="w-1/2"
              data-test-id="button-deny-sso"
            />
            <PrismButton
              @label={{t "login.accept_and_sign_in"}}
              @onClick={{@controller.confirmNewPlatform}}
              class="w-1/2"
            />
          </div>
        {{else}}
          {{#if @controller.showSubmitButton}}
            <PrismButton
              @isDisabled={{@controller.isSubmitButtonDisabled}}
              @label={{t "login.sign_in"}}
              @onClick={{@controller.submitForm}}
              class="mt-400"
              data-test-id="button-submit"
            />
          {{/if}}
          {{#if @controller.sharedAuth.ssoLoginOptions}}
            {{#if @controller.showSubmitButton}}
              <p
                class={{concat
                  "text-200--regular"
                  " "
                  styles.text-between-dividers
                  " "
                  "mt-600 flex items-center justify-center"
                }}
              >
                {{t "login.or_sign_in_with"}}
              </p>
            {{/if}}
            <div
              class="flex flex-col justify-between
                {{if @controller.showSubmitButton 'mt-300' 'mt-400'}}
                gap-200"
            >
              {{#each
                @controller.sharedAuth.ssoLoginOptions
                as |ssoLoginOption|
              }}
                <ButtonSingleSignOn
                  @isLoading={{eq
                    @controller.sharedAuth.loadingButtonType
                    ssoLoginOption.gipType
                  }}
                  @onClick={{ssoLoginOption.onClickAction}}
                  @type={{ssoLoginOption.type}}
                  data-test-id="button-sso"
                />
              {{/each}}
            </div>
          {{/if}}
        {{/if}}

        {{#unless @controller.sharedAuth.showSSOConfirmationScreen}}
          <LoginCardFooter
            @isRegistrationFlow={{false}}
            @showSignUpForTrial={{true}}
          />
        {{/unless}}
      </div>
    </LoginCard>
  {{/if}}
</template> satisfies TOC<LoginSignature>;
